import { hasGymModule } from '../utils/gymModules';
import GymPlanSelector from '../components/GymPlanSelector';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, TextField, Button, Stack, Divider,
  IconButton, Tab, Tabs, InputAdornment, FormControlLabel, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, Alert,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Avatar, MenuItem,
} from '@mui/material';
import { ArrowBack, Save, Upload, PersonAdd, Block, Delete, CheckCircle, Error as ErrorIcon, PhotoCamera, People } from '@mui/icons-material';
import { gymsAPI, uploadAPI, trainersAPI, membersAPI, usersAPI, SERVER_URL } from '../services/api';
import { CURRENCY_CODE, CURRENCY_SYMBOL } from '../utils/currency';
import { calculateOnboardingPricing, normalizeOnboardingPricing } from '../utils/onboardingPricing';
import { useAppData } from '../context/AppDataContext';

export default function GymDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { reloadGyms } = useAppData();
  const [gym, setGym] = useState(null);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    website: '',
    isActive: true,
    primaryColor: '#1A1A1A',
  });

  const [pricing, setPricing] = useState({
    monthly:   { price: '', features: [] },
    quarterly: { price: '', discount: '' },
    annual:    { price: '', discount: '' },
  });
  const [onboardingPricing, setOnboardingPricing] = useState(normalizeOnboardingPricing());

  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
  const isGymAdmin = currentUser?.role === 'gym_admin';
  const isSuperadmin = currentUser?.role === 'superadmin';

  const [trainers, setTrainers] = useState([]);
  const [members, setMembers] = useState([]);
  const [trainerForm, setTrainerForm] = useState({ name: '', email: '', password: '' });
  const [memberEmail, setMemberEmail] = useState('');
  const [memberPlanType, setMemberPlanType] = useState('monthly');
  const [adjustToCalendarMonth, setAdjustToCalendarMonth] = useState(false);
  const [tabAlert, setTabAlert] = useState({ type: '', message: '' });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [migrationOptions, setMigrationOptions] = useState({
    migrateUsers: true,
    migrateWorkoutPlans: true,
    migrateMealPlans: true,
    migrateExercises: true,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // Tab indices: 0=Details, 1=Trainers (gym_admin only), 2=Members (gym_admin only)
  // For superadmin: 0=Details only (no trainer/member tabs)
  const canManageTrainers = isGymAdmin && hasGymModule(gym, 'staff');
  const trainersTabIdx = canManageTrainers ? 1 : -1;
  const membersTabIdx = isGymAdmin ? (canManageTrainers ? 2 : 1) : -1;

  useEffect(() => {
    if (id !== 'new') loadGym();
    if (isGymAdmin) loadMembers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadGym = async () => {
    try {
      const response = await gymsAPI.getOne(id);
      const gymData = response.data.data;
      setGym(gymData);
      if (isGymAdmin && hasGymModule(gymData, 'staff')) loadTrainers();
      setFormData({
        name:         gymData.name         || '',
        description:  gymData.description  || '',
        address:      gymData.address      || '',
        city:         gymData.city         || '',
        state:        gymData.state        || '',
        zipCode:      gymData.zipCode      || '',
        phone:        gymData.phone        || '',
        email:        gymData.email        || '',
        website:      gymData.website      || '',
        isActive:     gymData.isActive !== undefined ? gymData.isActive : true,
        primaryColor: gymData.primaryColor || '#1A1A1A',
      });
      if (gymData.pricing) setPricing(gymData.pricing);
      setOnboardingPricing(normalizeOnboardingPricing(gymData.onboardingPricing));
    } catch (error) {
      console.error('Error loading gym:', error);
    }
  };

  // ── Logo upload ────────────────────────────────────────────────────────────
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const response = await uploadAPI.upload(file);
      const logoUrl = response.data.data.url;
      await gymsAPI.update(id, { logo: logoUrl });
      showSnackbar('Logo updated successfully!');
      loadGym();
    } catch (error) {
      showSnackbar('Failed to upload logo', 'error');
    } finally {
      setLogoUploading(false);
    }
  };

  // ── Save handlers ──────────────────────────────────────────────────────────
  const handleSaveDetails = async () => {
    setLoading(true);
    try {
      const details = { ...formData };
      if (!isSuperadmin) delete details.isActive;
      await gymsAPI.update(id, details);
      showSnackbar('Gym details updated successfully!');
      loadGym();
    } catch (error) {
      showSnackbar('Failed to update gym details', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePricing = async () => {
    setLoading(true);
    try {
      await gymsAPI.updatePricing(id, pricing);
      showSnackbar('Pricing updated successfully!');
    } catch (error) {
      showSnackbar('Failed to update pricing', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOnboardingPricing = async () => {
    setLoading(true);
    try {
      await gymsAPI.update(id, {
        onboardingPricing: calculateOnboardingPricing({
          ...onboardingPricing,
          currency: CURRENCY_CODE,
        }),
      });
      showSnackbar('Modules updated successfully!');
      reloadGyms();
      loadGym();
    } catch (error) {
      showSnackbar('Failed to update modules', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ── Trainer / Member handlers ──────────────────────────────────────────────
  const loadTrainers = async () => {
    try {
      const res = await trainersAPI.getAll();
      setTrainers(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const loadMembers = async () => {
    try {
      const res = await usersAPI.getAll({ gymId: currentUser?.gymId, role: 'user' });
      setMembers(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const handleAddTrainer = async () => {
    try {
      const res = await trainersAPI.create(trainerForm);
      setTrainers(t => [res.data.data, ...t]);
      setTrainerForm({ name: '', email: '', password: '' });
      setTabAlert({ type: 'success', message: 'Trainer added successfully' });
    } catch (e) {
      setTabAlert({ type: 'error', message: e.response?.data?.message || 'Failed to add trainer' });
    }
  };

  const handleDeactivateTrainer = async (trainerId) => {
    try {
      await trainersAPI.deactivate(trainerId);
      setTrainers(t => t.map(tr => tr._id === trainerId ? { ...tr, isActive: false } : tr));
      setTabAlert({ type: 'success', message: 'Trainer deactivated' });
    } catch (e) {
      setTabAlert({ type: 'error', message: 'Failed to deactivate trainer' });
    }
  };

  const handleAddMember = async () => {
    try {
      const userRes = await usersAPI.getAll({ search: memberEmail });
      const found = userRes.data.data?.[0];
      if (!found) { setTabAlert({ type: 'error', message: 'User not found' }); return; }
      await membersAPI.addToGym({ userId: found._id, gymId: currentUser?.gymId, planType: memberPlanType, adjustToCalendarMonth });
      setMembers(m => [...m, { ...found, membershipStatus: 'active' }]);
      setMemberEmail('');
      setTabAlert({ type: 'success', message: 'Member added to gym' });
    } catch (e) {
      setTabAlert({ type: 'error', message: e.response?.data?.message || 'Failed to add member' });
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await membersAPI.removeFromGym({ userId });
      setMembers(m => m.filter(mb => mb._id !== userId));
      setTabAlert({ type: 'success', message: 'Member removed from gym' });
    } catch (e) {
      setTabAlert({ type: 'error', message: 'Failed to remove member' });
    }
  };

  // ── Delete gym ─────────────────────────────────────────────────────────────
  const handleDeleteGym = async () => {
    setDeleting(true);
    try {
      const response = await gymsAPI.delete(id, migrationOptions);
      const migrated = response.data.data;
      setDeleteResult({ success: true, migrated, migrationOptions });
    } catch (e) {
      setDeleteResult({ success: false, error: e.response?.data?.message || 'Failed to delete gym' });
    } finally {
      setDeleting(false);
    }
  };

  if (!gym && id !== 'new') {
    return <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>;
  }

  const logoSrc = gym?.logo ? (gym.logo.startsWith('http') ? gym.logo : `${SERVER_URL}${gym.logo}`) : null;

  return (
    <Box>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={() => navigate('/dashboard/gyms')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {id === 'new' ? 'New Gym' : gym?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Update gym profile, enabled modules, pricing, trainers, and members from one place.
            </Typography>
          </Box>
        </Stack>
        {currentUser?.role === 'superadmin' && id !== 'new' && !gym?.isDefault && (
          <Button variant="outlined" color="error" startIcon={<Delete />} onClick={() => setDeleteDialogOpen(true)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}>
            Delete Gym
          </Button>
        )}
      </Stack>

      {/* ── Tabs ───────────────────────────────────────────────────────── */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Details" />
        {canManageTrainers && <Tab label="Trainers" />}
        {isGymAdmin && <Tab label="Members" />}
      </Tabs>

      {/* ── Details tab ────────────────────────────────────────────────── */}
      {tab === 0 && (
        <Stack spacing={3}>
          {/* Logo */}
          <Paper sx={{ p: 3, border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Gym Logo</Typography>
            <Stack direction="row" spacing={3} alignItems="center">
              <Avatar
                src={logoSrc || undefined}
                variant="rounded"
                sx={{ width: 96, height: 96, bgcolor: 'grey.100', border: '1px solid', borderColor: 'divider' }}
              >
                {!logoSrc && <PhotoCamera sx={{ fontSize: 36, color: 'text.disabled' }} />}
              </Avatar>
              <Box>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={logoUploading ? <CircularProgress size={16} /> : <Upload />}
                  disabled={logoUploading || id === 'new'}
                  size="small"
                >
                  {logoUploading ? 'Uploading…' : 'Upload Logo'}
                  <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                </Button>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Shown in the member app next to the gym name. PNG or JPG, max 2 MB.
                </Typography>
                {id === 'new' && (
                  <Typography variant="caption" color="warning.main" display="block">
                    Save the gym first, then upload a logo.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Paper>

          {/* Basic info */}
          <Paper sx={{ p: 3, border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Gym Information</Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Gym Name *" value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })} size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Email" type="email" value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })} size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Description" multiline rows={3} value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })} size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Address *" value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })} size="small" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="City *" value={formData.city}
                  onChange={e => setFormData({ ...formData, city: e.target.value })} size="small" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="State" value={formData.state}
                  onChange={e => setFormData({ ...formData, state: e.target.value })} size="small" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Zip Code" value={formData.zipCode}
                  onChange={e => setFormData({ ...formData, zipCode: e.target.value })} size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Phone" value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })} size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Website" value={formData.website}
                  onChange={e => setFormData({ ...formData, website: e.target.value })} size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                  App Theme Color
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box component="input" type="color" value={formData.primaryColor}
                    onChange={e => setFormData({ ...formData, primaryColor: e.target.value })}
                    sx={{ width: 48, height: 40, border: '1px solid #ddd', borderRadius: 1, cursor: 'pointer', padding: '2px' }} />
                  <TextField size="small" value={formData.primaryColor}
                    onChange={e => setFormData({ ...formData, primaryColor: e.target.value })}
                    placeholder="#1a1a2ed4" sx={{ width: 140 }} inputProps={{ maxLength: 7 }} />
                  <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: formData.primaryColor, border: '1px solid #ddd' }} />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Applied as the primary theme color in the member app
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Switch disabled={!isSuperadmin} checked={formData.isActive} onChange={e => setFormData({ ...formData, isActive: e.target.checked })} />}
                  label="Active"
                />
              </Grid>
            </Grid>
            <Button variant="contained" startIcon={<Save />} onClick={handleSaveDetails} disabled={loading} sx={{ mt: 3, minWidth: 140 }}>
              Save Details
            </Button>
          </Paper>

          {isSuperadmin && (
            <Paper sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <GymPlanSelector value={onboardingPricing} onChange={setOnboardingPricing} />
              <Button variant="contained" startIcon={<Save />} onClick={handleSaveOnboardingPricing} disabled={loading}
                sx={{ mt: 3, minWidth: 140 }}>
                Save Modules
              </Button>
            </Paper>
          )}

          {/* Membership Pricing */}
          <Paper sx={{ p: 3, border: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Membership Pricing</Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>Monthly</Typography>
                  <TextField fullWidth label="Price" type="number" size="small"
                    value={pricing.monthly?.price || ''}
                    onChange={e => setPricing({ ...pricing, monthly: { ...pricing.monthly, price: e.target.value } })}
                    InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment> }}
                    sx={{ mt: 1 }} />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>Quarterly</Typography>
                  <TextField fullWidth label="Price" type="number" size="small"
                    value={pricing.quarterly?.price || ''}
                    onChange={e => setPricing({ ...pricing, quarterly: { ...pricing.quarterly, price: e.target.value } })}
                    InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment> }}
                    sx={{ mt: 1 }} />
                  <TextField fullWidth label="Discount %" type="number" size="small"
                    value={pricing.quarterly?.discount || ''}
                    onChange={e => setPricing({ ...pricing, quarterly: { ...pricing.quarterly, discount: e.target.value } })}
                    sx={{ mt: 1.5 }} />
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>Annual</Typography>
                  <TextField fullWidth label="Price" type="number" size="small"
                    value={pricing.annual?.price || ''}
                    onChange={e => setPricing({ ...pricing, annual: { ...pricing.annual, price: e.target.value } })}
                    InputProps={{ startAdornment: <InputAdornment position="start">{CURRENCY_SYMBOL}</InputAdornment> }}
                    sx={{ mt: 1 }} />
                  <TextField fullWidth label="Discount %" type="number" size="small"
                    value={pricing.annual?.discount || ''}
                    onChange={e => setPricing({ ...pricing, annual: { ...pricing.annual, discount: e.target.value } })}
                    sx={{ mt: 1.5 }} />
                </Box>
              </Grid>
            </Grid>
            <Button variant="contained" startIcon={<Save />} onClick={handleSavePricing} disabled={loading}
              sx={{ mt: 3, minWidth: 140 }}>
              Save Pricing
            </Button>
          </Paper>
        </Stack>
      )}

      {/* ── Trainers tab (gym_admin only) ───────────────────────────────── */}
      {canManageTrainers && tab === trainersTabIdx && (
        <Paper sx={{ p: 3, border: 1, borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600} mb={2}>Trainers</Typography>
          {tabAlert.message && (
            <Alert severity={tabAlert.type || 'info'} sx={{ mb: 2 }} onClose={() => setTabAlert({ type: '', message: '' })}>
              {tabAlert.message}
            </Alert>
          )}

          {/* Add trainer form — Grid layout for better mobile */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
            <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5 }}>
              Add New Trainer
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField size="small" label="Name" value={trainerForm.name} fullWidth
                  onChange={e => setTrainerForm(f => ({ ...f, name: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField size="small" label="Email" value={trainerForm.email} fullWidth
                  onChange={e => setTrainerForm(f => ({ ...f, email: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField size="small" label="Password" type="password" value={trainerForm.password} fullWidth
                  onChange={e => setTrainerForm(f => ({ ...f, password: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" startIcon={<PersonAdd />} onClick={handleAddTrainer}
                  sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: 0, sm: 0 } }}>
                  Add Trainer
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1.5 } }}>
              <TableHead sx={{ '& .MuiTableCell-root': { bgcolor: 'background.paper', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' } }}>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trainers.map(tr => (
                  <TableRow key={tr._id} sx={{ '&:hover': { bgcolor: 'background.paper' } }}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{tr.name}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{tr.email}</Typography></TableCell>
                    <TableCell>
                      <Chip label={tr.isActive !== false ? 'Active' : 'Inactive'}
                        size="small"
                        sx={tr.isActive !== false
                          ? { bgcolor: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0', fontWeight: 700, fontSize: '0.7rem' }
                          : { bgcolor: 'background.paper', color: 'text.secondary', border: '1px solid', borderColor: 'divider', fontWeight: 700, fontSize: '0.7rem' }
                        } />
                    </TableCell>
                    <TableCell align="right">
                      {tr.isActive !== false && (
                        <Button size="small" color="error" startIcon={<Block />}
                          onClick={() => handleDeactivateTrainer(tr._id)}>Deactivate</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {trainers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 0, border: 0 }}>
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Box sx={{ width: 80, height: 80, borderRadius: 4, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                          <People sx={{ fontSize: 40, color: 'text.secondary' }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>No trainers yet</Typography>
                        <Typography variant="body2" color="text.secondary">Add your first trainer using the form above.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Members tab (gym_admin only) ────────────────────────────────── */}
      {isGymAdmin && tab === membersTabIdx && (
        <Paper sx={{ p: 3, border: 1, borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight={600} mb={2}>Members</Typography>
          {tabAlert.message && (
            <Alert severity={tabAlert.type || 'info'} sx={{ mb: 2 }} onClose={() => setTabAlert({ type: '', message: '' })}>
              {tabAlert.message}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3} alignItems="center">
            <TextField size="small" label="Search by email" value={memberEmail}
              onChange={e => setMemberEmail(e.target.value)} sx={{ flexGrow: 1 }} />

            <TextField
              select
              size="small"
              label="Plan"
              value={memberPlanType}
              onChange={e => setMemberPlanType(e.target.value)}
              sx={{ width: 160 }}
            >
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="annual">Annual</MenuItem>
            </TextField>

            <FormControlLabel
              control={<Switch checked={adjustToCalendarMonth} onChange={e => setAdjustToCalendarMonth(e.target.checked)} />}
              label="Adjust to calendar month"
            />

            <Button variant="contained" startIcon={<PersonAdd />} onClick={handleAddMember}
              sx={{ width: { xs: '100%', sm: 'auto' } }}>
              Add Member
            </Button>
          </Stack>
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 1.5 } }}>
              <TableHead sx={{ '& .MuiTableCell-root': { bgcolor: 'background.paper', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' } }}>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Membership</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map(mb => (
                  <TableRow key={mb._id} sx={{ '&:hover': { bgcolor: 'background.paper' } }}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{mb.name}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{mb.email}</Typography></TableCell>
                    <TableCell>
                      <Chip label={mb.membershipStatus || 'inactive'}
                        size="small"
                        sx={mb.membershipStatus === 'active'
                          ? { bgcolor: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0', fontWeight: 700, fontSize: '0.7rem' }
                          : { bgcolor: 'background.paper', color: 'text.secondary', border: '1px solid', borderColor: 'divider', fontWeight: 700, fontSize: '0.7rem' }
                        } />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" color="error" onClick={() => handleRemoveMember(mb._id)}>Remove</Button>
                    </TableCell>
                  </TableRow>
                ))}
                {members.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 0, border: 0 }}>
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Box sx={{ width: 80, height: 80, borderRadius: 4, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                          <PersonAdd sx={{ fontSize: 40, color: 'text.secondary' }} />
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', mb: 1 }}>No members yet</Typography>
                        <Typography variant="body2" color="text.secondary">Search by email above to add members to this gym.</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* ── Delete Gym Dialog ───────────────────────────────────────────── */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => { if (!deleting) { setDeleteDialogOpen(false); setDeleteResult(null); } }}
        maxWidth="sm" fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            {deleteResult?.success
              ? <CheckCircle color="success" />
              : deleteResult?.error
                ? <ErrorIcon color="error" />
                : <Delete color="error" fontSize="small" />}
            <Typography variant="h6">
              {deleteResult?.success ? 'Gym Deleted' : deleteResult?.error ? 'Delete Failed' : 'Delete Gym'}
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {deleteResult ? (
            deleteResult.success ? (
              <Stack spacing={2}>
                <Alert severity="success"><strong>{gym?.name}</strong> has been permanently deleted.</Alert>
                {Object.values(deleteResult.migrationOptions).some(Boolean) && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>Migrated to default gym:</Typography>
                    <Stack spacing={0.5}>
                      {deleteResult.migrationOptions.migrateUsers && <Typography variant="body2">• {deleteResult.migrated.migratedUsers || 0} users</Typography>}
                      {deleteResult.migrationOptions.migrateWorkoutPlans && <Typography variant="body2">• {deleteResult.migrated.migratedWorkoutPlans || 0} workout plans</Typography>}
                      {deleteResult.migrationOptions.migrateMealPlans && <Typography variant="body2">• {deleteResult.migrated.migratedMealPlans || 0} meal plans</Typography>}
                      {deleteResult.migrationOptions.migrateExercises && <Typography variant="body2">• {deleteResult.migrated.migratedExercises || 0} exercises</Typography>}
                    </Stack>
                  </Box>
                )}
              </Stack>
            ) : (
              <Alert severity="error">{deleteResult.error}</Alert>
            )
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight={600} gutterBottom>Warning: This will permanently delete the gym!</Typography>
                <Typography variant="body2">Choose what to do with the gym's data below.</Typography>
              </Alert>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Migration Options</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select which data to migrate to the default gym. Unchecked items will be permanently deleted.
              </Typography>
              <Stack spacing={1.5}>
                {[
                  { key: 'migrateUsers',        label: 'Migrate Users',         sub: 'Move all users to the default gym' },
                  { key: 'migrateWorkoutPlans', label: 'Migrate Workout Plans', sub: 'Move all workout plans to the default gym' },
                  { key: 'migrateMealPlans',    label: 'Migrate Meal Plans',    sub: 'Move all meal plans to the default gym' },
                  { key: 'migrateExercises',    label: 'Migrate Exercises',     sub: 'Move all exercises to the default gym' },
                ].map(({ key, label, sub }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        checked={migrationOptions[key]}
                        onChange={e => setMigrationOptions({ ...migrationOptions, [key]: e.target.checked })}
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={600}>{label}</Typography>
                        <Typography variant="caption" color="text.secondary">{sub}</Typography>
                      </Box>
                    }
                  />
                ))}
              </Stack>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          {deleteResult?.success ? (
            <Button variant="contained" onClick={() => navigate('/dashboard/gyms')}>Back to Gyms</Button>
          ) : deleteResult?.error ? (
            <>
              <Button onClick={() => setDeleteResult(null)}>Try Again</Button>
              <Button onClick={() => { setDeleteDialogOpen(false); setDeleteResult(null); }}>Cancel</Button>
            </>
          ) : (
            <>
              <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
              <Button variant="contained" color="error" onClick={handleDeleteGym} disabled={deleting}>
                {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete Gym'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Box>
  );
}
